import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Short-lived download token cache — replaces the session JWT in query-string
// URLs (<img src>, <a href>) so the JWT never leaks into logs/history.
let cached = { token: "", exp: 0 };
let inflight = null;
let suspended = false; // set on logout so stray renders don't 401-spam
const listeners = new Set();

export function getDownloadToken() {
    if (!cached.token || Date.now() > cached.exp) ensureDownloadToken();
    return cached.token;
}

export async function ensureDownloadToken(force = false) {
    if (force) suspended = false;
    if (suspended) return "";
    if (cached.token && Date.now() < cached.exp) return cached.token;
    if (!inflight) {
        inflight = api
            .post("/auth/download-token")
            .then(({ data }) => {
                cached = {
                    token: data.token,
                    exp: Date.now() + Math.max(30, (data.expires_in || 900) - 120) * 1000,
                };
                listeners.forEach((fn) => fn(cached.token));
                return cached.token;
            })
            .catch(() => "")
            .finally(() => {
                inflight = null;
            });
    }
    return inflight;
}

export function clearDownloadToken() {
    suspended = true;
    cached = { token: "", exp: 0 };
}

export function useDownloadToken() {
    const [tok, setTok] = useState(cached.token);
    useEffect(() => {
        const fn = (t) => setTok(t);
        listeners.add(fn);
        ensureDownloadToken().then((t) => t && setTok(t));
        return () => listeners.delete(fn);
    }, []);
    return tok;
}
